import { cloneDeep, get } from "lodash";
import { useTranslation } from "react-i18next";
import { invariant } from "@searchui-searchforms/shared/utils";
import type { GivenNameExactness, SurnameExactness } from "@/entities/data-model";
import { AboutTheseSettingsLink, SEARCH_TIPS_EXACT_ANCHOR } from "@/shared/components/AboutTheseSettingsLink";
import { useFormAPI, useFormStore } from "@/shared/forms";
import { useFieldContext } from "../FieldContext";
import { ExactnessButton } from "./ExactnessButton";
import { ExactnessMultiSelect } from "./ExactnessMultiSelect";
import { getMatchLabelKey, getOptionIds, idToTranslationKey } from "./name-exactness-utils";

export const NameExactness = () => {
    const { t } = useTranslation();
    const { setFieldValue } = useFormAPI();
    const { fieldPath, exactnessFieldPath, fieldConfig } = useFieldContext();

    invariant(exactnessFieldPath, "exactnessFieldPath is required");

    const hasName = useFormStore(({ values }) => !!get(values, fieldPath));
    const nameExactnessValue = useFormStore(({ values }) => get(values, exactnessFieldPath)) as
        | GivenNameExactness
        | SurnameExactness;

    // A name field that is required for search must be an exact match — you can't fuzzy-match
    // a token the search engine mandates. So its exactness control is locked to a static
    // "Exact" (no dropdown), matching legacy behavior. Applies to any required name field
    // (surname today; first name too if it ever becomes required). See TT-3965.
    //
    // The value itself is seeded exact-only by the metadata converter (populateNameFields) and
    // kept exact-only by the MAT toggle (useToggleNameExactness), so this component stays purely
    // presentational — it only chooses which control to render, never writes the locked value.
    const isExactnessLocked = !!fieldConfig.required;

    const optionIds = getOptionIds(nameExactnessValue.flags);
    const selectedIds = optionIds.filter((id) => nameExactnessValue.flags[id as keyof typeof nameExactnessValue.flags]);

    const value = {
        isExact: nameExactnessValue.isExact,
        selectedOptionIds: selectedIds,
    };

    const options = optionIds.map((id) => ({
        id,
        label: t(idToTranslationKey[id]),
    }));

    if (!hasName) return null;

    if (isExactnessLocked) {
        return (
            <ExactnessButton isExact locked testId={exactnessFieldPath} onClick={() => {}}>
                {t("name.exactness.exact")}
            </ExactnessButton>
        );
    }

    const baseLabel = t(getMatchLabelKey(nameExactnessValue.flags));
    const label = nameExactnessValue.isExact ? baseLabel : `${baseLabel}...`;

    return (
        <ExactnessMultiSelect
            value={value}
            options={options}
            onChange={(selectedOption) => {
                const nextExactnessFieldValue = cloneDeep(nameExactnessValue);
                nextExactnessFieldValue.isExact = selectedOption.isExact;
                optionIds.forEach((key) => {
                    nextExactnessFieldValue.flags[key as keyof typeof nextExactnessFieldValue.flags] =
                        selectedOption.selectedOptionIds.includes(key);
                });
                setFieldValue(exactnessFieldPath, nextExactnessFieldValue);
            }}
            label={label}
            testId={exactnessFieldPath}
            footer={<AboutTheseSettingsLink anchor={SEARCH_TIPS_EXACT_ANCHOR} />}
        />
    );
};
